import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "@/stores/hook";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  GraduationCap,
  Layers,
  Download,
  FileText,
  Receipt,
  Loader2,
  ArrowRight,
  User,
  IndianRupee,
  TrendingDown,
  Wallet,
  Clock,
  CheckCircle2,
  CreditCard,
  Building2,
} from "lucide-react";
import {
  clearDealPreview,
  setIsProgramDetails,
  setProgramDetailId,
  setShowDealPreview,
  setDealPreviewData,
} from "@/features/academy/stores/academyFunctionalSlice";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { childEndpoints } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { refetchCurrentChild } from "@/features/leads/api/useCurrentChild";
import { refetchChilds } from "@/features/leads/api/useChilds";
import { toast } from "sonner";
import {
  generateAcademyDocumentPdf,
  getNextDocumentNumber,
} from "@/module/academy/helpers/generateAcademyDocumentPdf";
import { queryClient } from "@/lib/queryClient";
import { refreshAfterDealCreate } from "@/module/utils/refreshAfterDealCreate";
import DealPreviewPrefrences from "@/components/ui/others/PrefrenceRenderers/DealPreviewPrefrences";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function AcademyDealPreview() {
  const dispatch = useAppDispatch();
  const { dealPreviewData } = useAppSelector((s) => s.academyFunctional);
  const {
    currentUser: {
      user: { tenant },
    },
  } = useAppSelector((state) => state.auth);
  const { lead, refetchLead, handleChildChange } = useActiveDataMutation();

  const [creating, setCreating] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [customInputScopes, setCustomInputScopes] = useState<any[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  const data = dealPreviewData;

  const previewModuleScopes = useMemo(() => {
    if (!data?.deals?.length) return [];
    const uniqueIds = [...new Set(data.deals.map((d: any) => d.variantId))];
    return uniqueIds.map((id) => ({
      nicheModelName: "academyProgramVariant",
      nicheItemId: id,
    }));
  }, [data?.deals]);

  const handleScopesChange = useCallback((scopes: any[]) => {
    setCustomInputScopes(scopes);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const paidPercent = useMemo(() => {
    if (!data || data.netAmount === 0) return 0;
    return Math.min(100, (data.advance / data.netAmount) * 100);
  }, [data]);

  if (!data) {
    return (
      <div className="p-8 text-center text-[#697571]">
        No preview data available.
      </div>
    );
  }

  const handleBack = () => {
    dispatch(setShowDealPreview(false));
  };

  const handleDownload = (type: "invoice" | "quotation") => {
    setShowDownloadMenu(false);
    const tenantId = tenant?.id || "0";
    const documentNumber = getNextDocumentNumber(type, tenantId);

    let addressStr = "";
    let gstinStr = "";
    let paymentInfoStr = "";
    try {
      const parsed = JSON.parse(tenant?.tenantAddress || "{}");
      addressStr = parsed.address || "";
      gstinStr = parsed.gstin || "";
      paymentInfoStr = parsed.paymentInfo || "";
    } catch {
      addressStr = tenant?.tenantAddress || "";
    }

    generateAcademyDocumentPdf({
      type,
      previewData: data,
      tenant: {
        tenantName: tenant?.tenantName || "",
        tenantLogoUrl: tenant?.tenantLogoUrl || "",
        tenantAddress: addressStr,
        gstin: gstinStr,
        paymentInfo: paymentInfoStr,
      },
      clientName: lead?.name || lead?.phone || "Client",
      documentNumber,
    });

    toast.success(
      `${type === "invoice" ? "Invoice" : "Quotation"} ${documentNumber} downloaded`
    );
  };

  const closeDealPreview = () => {
    dispatch(clearDealPreview());
    dispatch(setIsProgramDetails(false));
    dispatch(setProgramDetailId(null));
    dispatch(setShowDealPreview(false));
    dispatch(setDealPreviewData(null));
  };

  const handleCreateDeals = async () => {
    if (!lead?.id || data.deals.length === 0) return;
    setCreating(true);
    let lastChildId: number | null = null;
    let successCount = 0;
    const createdChildIds: number[] = [];

    const dealCount = data.deals.length;
    const perDealAdvance = dealCount > 0 ? data.advance / dealCount : 0;

    try {
      for (const deal of data.deals) {
        const totalDealValue = deal.courseFees;
        const discountAmount = deal.discount ?? 0;
        const netAmount = totalDealValue - discountAmount;
        const advanceAmount = perDealAdvance;

        const res = await axiosInstance.post(childEndpoints, {
          name: deal.name,
          leadId: lead.id,
          moduleScopes: [
            {
              nicheModelName: "academyProgramVariant",
              nicheItemId: deal.variantId,
            },
          ],
          ...(customInputScopes.length > 0 && { customInputScopes }),
          financials: {
            totalDealValue,
            discountType: discountAmount > 0 ? "amount" : undefined,
            discountValue: discountAmount,
            discountAmount,
            netAmount,
            advanceAmount,
            advanceMethod: advanceAmount > 0 ? data.advanceMethod : undefined,
            advanceBankName:
              advanceAmount > 0 ? data.advanceBankName : undefined,
            advanceReceiptDocId:
              advanceAmount > 0 ? data.advanceReceiptDocId : undefined,
          },
        });
        const childId = res?.data?.child?.id;
        if (childId) {
          lastChildId = childId;
          createdChildIds.push(childId);
        }
        successCount++;
      }
      toast.success(
        `${successCount} deal${successCount > 1 ? "s" : ""} created`
      );
      refreshAfterDealCreate(lead.id);
      await Promise.all([
        refetchCurrentChild(),
        refetchChilds(),
        refetchLead(),
      ]);
      if (lastChildId) handleChildChange(lastChildId);

      if (data.advance > 0 && createdChildIds.length > 0) {
        await autoUpdateAdvancePayments(createdChildIds);
      }
      closeDealPreview();
    } catch (err: any) {
      if (successCount > 0) {
        toast.warning(
          `${successCount} of ${data.deals.length} deals created before error`
        );
        refreshAfterDealCreate(lead.id);
        await Promise.all([
          refetchCurrentChild(),
          refetchChilds(),
          refetchLead(),
        ]);
        if (lastChildId) handleChildChange(lastChildId);
        closeDealPreview();
      } else {
        toast.error(err?.response?.data?.message || "Failed to create deals");
      }
    } finally {
      setCreating(false);
    }
  };

  const autoUpdateAdvancePayments = async (childIds: number[]) => {
    const autoNote = `Advance split across ${data.deals.map((d) => d.name).join(", ")}`;
    try {
      const patchBody: any = {
        method: data.advanceMethod || "UPI",
        note: autoNote,
      };
      if (data.advanceBankName) patchBody.txnId = data.advanceBankName;
      if (data.advanceReceiptDocId != null && data.advanceReceiptDocId > 0) {
        patchBody.receiptDocId = data.advanceReceiptDocId;
      }

      // Each child's advance payment is independent — run them in parallel
      // instead of N serial GET+PATCH round-trips, so a multi-deal submit
      // doesn't make the user wait on a serial chain. (The deal-CREATION loop
      // above stays serial: concurrent child creates on one lead can race on
      // server-side ordering, and serial keeps the partial-success count.)
      await Promise.all(
        childIds.map(async (childId) => {
          const res = await axiosInstance.get(`/core/payment/child/${childId}`);
          const advPayment = res?.data?.payments?.find(
            (p: any) => p.method === "Advance"
          );
          if (advPayment) {
            await axiosInstance.patch(`/core/payment/${advPayment.id}`, patchBody);
          }
        })
      );
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["childs"] });
      queryClient.invalidateQueries({ queryKey: ["payment-summary"] });
    } catch {
      /* silent best-effort */
    }
  };

  const totalDeals = data.deals.length;

  return (
    <div className="flex flex-col h-full bg-[#f7f9f8]">
      <div className="px-5 pt-4 pb-3 bg-white border-b border-[#e6e8e7] sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="size-8 rounded-full flex items-center justify-center hover:bg-[#f0f2f1] transition-colors"
          >
            <ChevronLeft className="size-5 text-[#374741]" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#051912] tracking-[-0.3px]">
              Deal Preview
            </h2>
            <p className="text-[11px] text-[#9ba3a0] mt-0.5">
              Review before creating {totalDeals} deal
              {totalDeals > 1 ? "s" : ""}
            </p>
          </div>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="h-8 px-3 flex items-center gap-1.5 rounded-full border border-[#e6e8e7] bg-white text-[12px] font-medium text-[#374741] hover:bg-[#f7f9f8] transition-colors"
            >
              <Download className="size-3.5" />
              Export
            </button>

            {showDownloadMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border border-[#e6e8e7] shadow-xl z-50 min-w-[160px] overflow-hidden">
                <button
                  onClick={() => handleDownload("quotation")}
                  className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left hover:bg-[#f0faf5] transition-colors"
                >
                  <FileText className="size-3.5 text-[#267355]" />
                  <span className="text-[12px] font-medium text-[#051912]">
                    Quotation
                  </span>
                </button>
                <div className="h-px bg-[#f0f1f0]" />
                <button
                  onClick={() => handleDownload("invoice")}
                  className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left hover:bg-[#f0faf5] transition-colors"
                >
                  <Receipt className="size-3.5 text-[#267355]" />
                  <span className="text-[12px] font-medium text-[#051912]">
                    Invoice
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-4 flex flex-col gap-4">
          <div className="flex items-center gap-2.5 px-1">
            <div className="size-7 rounded-lg bg-gradient-to-br from-[#E9FCF5] to-[#d1f5e8] flex items-center justify-center">
              <GraduationCap className="size-3.5 text-[#267355]" />
            </div>
            <div className="min-w-0">
              <p className="text-[14px] font-bold text-[#051912] truncate">
                {data.programName}
              </p>
              {data.programCode && (
                <p className="text-[10px] text-[#9ba3a0] font-mono">
                  {data.programCode}
                </p>
              )}
            </div>
          </div>

          {data.entries.map((entry) => {
            const variantDeals = data.deals.filter(
              (d) => d.variantId === entry.variantId
            );
            return (
              <div
                key={entry.variantId}
                className="rounded-2xl bg-white border border-[#e6e8e7] overflow-hidden shadow-sm"
              >
                <div className="px-4 py-3 bg-gradient-to-r from-[#f7f9f8] to-white border-b border-[#f0f1f0]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="size-7 rounded-lg bg-[#E9FCF5] flex items-center justify-center shrink-0">
                        <Layers className="size-3.5 text-[#267355]" />
                      </div>
                      <p className="text-[13px] font-semibold text-[#051912] truncate">
                        {entry.variantName}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[15px] font-bold text-[#051912] tabular-nums">
                        ₹{fmt(entry.courseFees)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="px-3 py-2">
                  <div className="grid grid-cols-2 gap-2 text-[11px] mb-2">
                    <div className="rounded-lg bg-[#fafbfb] px-3 py-2">
                      <span className="text-[#9ba3a0]">Course Fees</span>
                      <p className="font-semibold text-[#051912] tabular-nums">
                        ₹{fmt(entry.courseFees)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-[#fafbfb] px-3 py-2">
                      <span className="text-[#9ba3a0]">Discount</span>
                      <p className="font-semibold text-[#EF5350] tabular-nums">
                        -₹{fmt(entry.discountAllowed)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    {variantDeals.map((deal, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2.5 py-2 px-3 rounded-xl bg-[#fafbfb] border border-[#f0f1f0]"
                      >
                        <div className="size-6 rounded-full bg-[#E9FCF5] flex items-center justify-center shrink-0">
                          <User className="size-3 text-[#267355]" />
                        </div>
                        <span className="text-[12px] font-medium text-[#374741] truncate flex-1">
                          {deal.name}
                        </span>
                        <span className="text-[12px] font-semibold text-[#051912] tabular-nums shrink-0">
                          ₹{fmt(deal.courseFees)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {previewModuleScopes.length > 0 && (
            <DealPreviewPrefrences
              moduleScopes={previewModuleScopes}
              onScopesChange={handleScopesChange}
            />
          )}

          <div className="rounded-2xl bg-white border border-[#e6e8e7] overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-[#051912]">
              <p className="text-[11px] font-semibold text-white/60 uppercase tracking-wider">
                Financial Summary
              </p>
            </div>

            <div className="px-4 py-3.5 flex flex-col gap-0">
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <IndianRupee className="size-3.5 text-[#9ba3a0]" />
                  <span className="text-[13px] text-[#697571]">
                    Gross Total
                  </span>
                </div>
                <span className="text-[14px] font-semibold text-[#051912] tabular-nums">
                  ₹{fmt(data.grossTotal)}
                </span>
              </div>

              {data.discountAmount > 0 && (
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="size-3.5 text-[#EF5350]" />
                    <span className="text-[13px] text-[#697571]">
                      {data.discountType === "percentage"
                        ? `Discount (${data.discountValue}%)`
                        : "Discount"}
                    </span>
                  </div>
                  <span className="text-[14px] font-semibold text-[#EF5350] tabular-nums">
                    −₹{fmt(data.discountAmount)}
                  </span>
                </div>
              )}

              <div className="border-t border-[#f0f1f0] mt-1 pt-2">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Wallet className="size-3.5 text-[#051912]" />
                    <span className="text-[14px] font-bold text-[#051912]">
                      Net Amount
                    </span>
                  </div>
                  <span className="text-[16px] font-bold text-[#051912] tabular-nums">
                    ₹{fmt(data.netAmount)}
                  </span>
                </div>
              </div>

              {data.advance > 0 && (
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-3.5 text-[#267355]" />
                    <span className="text-[13px] text-[#267355] font-medium">
                      Advance Collected
                    </span>
                  </div>
                  <span className="text-[14px] font-bold text-[#267355] tabular-nums">
                    ₹{fmt(data.advance)}
                  </span>
                </div>
              )}

              {data.advance > 0 &&
                (data.advanceMethod || data.advanceBankName) && (
                  <div className="flex items-center gap-2 py-1.5 px-8">
                    {data.advanceMethod && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#697571] bg-[#f7f9f8] px-2 py-0.5 rounded-full border border-[#f0f1f0]">
                        <CreditCard className="size-2.5" />
                        {data.advanceMethod}
                      </span>
                    )}
                    {data.advanceBankName && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#697571] bg-[#f7f9f8] px-2 py-0.5 rounded-full border border-[#f0f1f0]">
                        <Building2 className="size-2.5" />
                        {data.advanceBankName}
                      </span>
                    )}
                  </div>
                )}

              {data.advance > 0 && (
                <div className="mt-2 mb-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-[#9ba3a0]">
                      Collection Progress
                    </span>
                    <span className="text-[10px] font-semibold text-[#267355]">
                      {paidPercent.toFixed(0)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#e6e8e7] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#267355] to-[#46a580] transition-all"
                      style={{ width: `${paidPercent}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="border-t border-[#f0f1f0] mt-2 pt-2">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <Clock className="size-3.5 text-[#EA580C]" />
                    <span className="text-[14px] font-bold text-[#EA580C]">
                      Pending
                    </span>
                  </div>
                  <span className="text-[16px] font-bold text-[#EA580C] tabular-nums">
                    ₹{fmt(data.pending)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 py-1 text-[11px] text-[#9ba3a0]">
            <span className="size-1 rounded-full bg-[#cdd1d0]" />
            {data.entries.length} variant{data.entries.length > 1 ? "s" : ""}{" "}
            &middot; {totalDeals} deal{totalDeals > 1 ? "s" : ""}
            <span className="size-1 rounded-full bg-[#cdd1d0]" />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-white border-t border-[#e6e8e7] px-4 py-3 flex gap-3">
        <Button
          variant="outline"
          onClick={handleBack}
          className="flex-1 h-11 rounded-xl gap-1.5 text-[13px] font-medium border-[#e6e8e7]"
          disabled={creating}
        >
          <ChevronLeft className="size-4" />
          Edit
        </Button>
        <Button
          variant="primary"
          onClick={handleCreateDeals}
          disabled={creating || totalDeals === 0}
          className="flex-[2] h-11 rounded-xl gap-1.5 text-[13px] font-bold"
        >
          {creating ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              Create {totalDeals} Deal{totalDeals > 1 ? "s" : ""}
              <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
